import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { HeroAnimation } from './HeroAnimation';
import { Ship, Cpu, X, Wrench, Box, ScanLine, RotateCcw } from 'lucide-react';

interface HeroProps {
  className?: string;
}

type ActivePanel = 'repairs' | 'engineering' | null;

const Hero = ({ className }: HeroProps) => {
  const { t } = useTranslation();
  const [active, setActive] = useState<ActivePanel>(null);

  const toggle = (panel: ActivePanel) => {
    setActive(prev => (prev === panel ? null : panel));
  };

  const repairsFeatures = [
    { icon: Ship, label: t('footer.services.marine') },
    { icon: Wrench, label: t('footer.services.welding') },
  ];
  const engineeringFeatures = [
    { icon: Box, label: t('footer.services.containers') },
    { icon: ScanLine, label: t('footer.services.scanning') },
    { icon: RotateCcw, label: t('footer.services.reverse') },
  ];

  return (
    <section
      id="home"
      data-surface="dark"
      className="relative scroll-mt-[-32px] md:scroll-mt-2 min-h-screen flex flex-col bg-navy-dark overflow-hidden"
    >
      <HeroAnimation />

      {/* ── Desktop overlay title (hidden on mobile) ── */}
      <AnimatePresence>
        {active === null && (
          <motion.div
            key="hero-title"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="hidden md:flex absolute inset-x-0 top-[28%] z-30 flex-col items-center pointer-events-none"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-foreground dark:text-white leading-[1.1] text-center drop-shadow-sm dark:drop-shadow-2xl">
              {t('hero.title')}
            </h1>
            <p className="mt-3 text-foreground/50 dark:text-white/30 text-xs tracking-widest">
              ↓ wybierz oddział &mdash; kliknij, aby rozwinąć
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Mobile hero header (shown only on mobile) ── */}
      <div className="md:hidden relative z-30 flex flex-col items-center justify-center pt-28 pb-6 px-6 text-center pointer-events-none">
        <h1 className="text-4xl font-black text-foreground dark:text-white leading-[1.1] drop-shadow-sm dark:drop-shadow-2xl">
          {t('hero.title')}
        </h1>
        <p className="mt-3 text-foreground/50 dark:text-white/40 text-xs tracking-widest">
          ↓ wybierz oddział &mdash; kliknij, aby rozwinąć
        </p>
      </div>

      {/* ── SPLIT PANEL — fills full height ── */}
      <div className="relative z-10 flex flex-1 flex-col md:flex-row min-h-0">

        {/* ===== REPAIRS & OUTFITTING ===== */}
        <div
          onClick={() => toggle('repairs')}
          className={`
            group relative flex flex-col justify-end items-center overflow-hidden cursor-pointer
            md:transition-[flex] md:duration-700 md:ease-in-out
            ${active === 'engineering' ? 'md:flex-[0.35]' : active === 'repairs' ? 'md:flex-[0.65]' : 'md:flex-[1]'}
            ${active === 'engineering' ? 'hidden md:flex' : ''}
            min-h-[45vw] md:min-h-0
          `}
        >
          {/* Background gradient — light blue in light mode, dark navy in dark mode */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-200/80 via-blue-50/90 to-white dark:from-blue-900/60 dark:via-[#152440]/80 dark:to-[#152440]/90" />
          {/* Hover glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 dark:from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Vertical accent line on right (desktop only) */}
          <div className="hidden md:block absolute right-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-blue-400/60 dark:via-blue-500/40 to-transparent z-30" />

          {/* Horizontal separator line at bottom (mobile only) */}
          <div className="md:hidden absolute bottom-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-blue-400/60 dark:via-blue-500/40 to-transparent z-30" />

          {/* Content */}
          <div className="relative z-20 flex flex-col items-center text-center px-6 py-8 md:p-12 w-full">
            <span className="block text-blue-700/80 dark:text-blue-400/70 text-[10px] font-bold tracking-[0.2em] uppercase mb-3">
              {t('hero.services.repairs.label')}
            </span>

            <div className="flex flex-col items-center gap-1 mb-2">
              <p className="text-foreground/30 dark:text-white/40 text-xs font-bold tracking-widest uppercase">REMAR</p>
              <h2 className="text-2xl md:text-3xl font-black text-foreground dark:text-white leading-tight">
                {t('hero.services.repairs.title')}
              </h2>
            </div>

            {/* Tap hint on mobile */}
            <p className="md:hidden mt-2 text-foreground/30 dark:text-white/25 text-[10px] tracking-wider">
              dotknij, aby rozwinąć
            </p>

            {/* Expandable description */}
            <AnimatePresence>
              {active === 'repairs' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="overflow-hidden w-full mt-5"
                >
                  <p className="text-steel-light text-sm md:text-base leading-relaxed mb-5 max-w-md mx-auto">
                    {t('hero.services.repairs.description')}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {repairsFeatures.map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                        <Icon size={12} />
                        {label}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ===== ENGINEERING ===== */}
        <div
          onClick={() => toggle('engineering')}
          className={`
            group relative flex flex-col justify-end items-center overflow-hidden cursor-pointer
            md:transition-[flex] md:duration-700 md:ease-in-out
            ${active === 'repairs' ? 'md:flex-[0.35]' : active === 'engineering' ? 'md:flex-[0.65]' : 'md:flex-[1]'}
            ${active === 'repairs' ? 'hidden md:flex' : ''}
            min-h-[45vw] md:min-h-0
          `}
        >
          {/* Background gradient — light blue in light mode, dark navy in dark mode */}
          <div className="absolute inset-0 bg-gradient-to-bl from-sky-200/70 via-sky-50/80 to-white dark:from-sky-900/40 dark:via-[#152440]/80 dark:to-[#152440]/95" />
          {/* Hover glow */}
          <div className="absolute inset-0 bg-gradient-to-bl from-sky-400/10 dark:from-sky-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Content */}
          <div className="relative z-20 flex flex-col items-center text-center px-6 py-8 md:p-12 w-full">
            <span className="block text-sky-600/80 dark:text-sky-400/70 text-[10px] font-bold tracking-[0.2em] uppercase mb-3">
              od 2022 roku
            </span>

            <div className="flex flex-col items-center gap-1 mb-2">
              <p className="text-foreground/30 dark:text-white/40 text-xs font-bold tracking-widest uppercase">REMAR</p>
              <h2 className="text-2xl md:text-3xl font-black text-foreground dark:text-white tracking-widest uppercase leading-tight">
                {t('hero.services.engineering.title')}
              </h2>
            </div>

            {/* Tap hint on mobile */}
            <p className="md:hidden mt-2 text-foreground/30 dark:text-white/25 text-[10px] tracking-wider">
              dotknij, aby rozwinąć
            </p>

            <AnimatePresence>
              {active === 'engineering' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="overflow-hidden w-full mt-5"
                >
                  <p className="text-steel-light text-sm md:text-base leading-relaxed mb-5 max-w-md mx-auto">
                    {t('hero.services.engineering.description')}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {engineeringFeatures.map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-500/15 border border-sky-500/25 text-sky-700 dark:text-sky-300 text-xs font-medium">
                        <Icon size={12} />
                        {label}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
